import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { AddressGroup } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  addressGroups: AddressGroup[];
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function MergeAddressesModal({ open, onClose, addressGroups, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await call("/recharge/address/merge", {
        method: "POST",
        body: { sourceAddressId: sourceId, targetAddressId: targetId },
      });
      if (!res.ok) throw new Error(res.error || "Failed to merge addresses");
      onSuccess("Addresses merged successfully!");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to merge addresses");
    } finally {
      setLoading(false);
    }
  };

  const renderAddress = (g: AddressGroup) =>
    g.address ? `${g.address.address1}, ${g.address.city} (${g.allSubs.length} subs)` : `ID: ${g.addressId}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800 }}>Merge Addresses</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="warning" sx={{ mb: 2 }}>
          All subscriptions from the source address will be moved to the target address. The source address may be removed.
        </Alert>
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel>Source Address (will be merged away)</InputLabel>
            <Select value={sourceId} onChange={(e) => setSourceId(e.target.value)} label="Source Address (will be merged away)">
              {addressGroups.filter(g => g.addressId !== targetId).map(g => (
                <MenuItem key={g.addressId} value={g.addressId}>{renderAddress(g)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Target Address (keep)</InputLabel>
            <Select value={targetId} onChange={(e) => setTargetId(e.target.value)} label="Target Address (keep)">
              {addressGroups.filter(g => g.addressId !== sourceId).map(g => (
                <MenuItem key={g.addressId} value={g.addressId}>{renderAddress(g)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || !sourceId || !targetId || sourceId === targetId}
          sx={{ bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark }, textTransform: "none" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Merge"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
